import { useEffect, useState } from "react";
import { api } from "../../api";
import { useAuth } from "../../auth";
import { useSubView } from "../../App";
import { Btn, Card, Badge, PageHead } from "../../components/ui";
import Icon from "../../components/Icon";
import CourseEditor from "./CourseEditor2";
import TrainerCourseView from "./TrainerCourseView";

// Sub-view: null (list), { mode: "edit", id, viewOnly? } or { mode: "trainer", id }
const encodeCourseView = (v) => v;
const decodeCourseView = (v) => v;

export default function Courses({ toast, can }) {
  const { user } = useAuth();
  const _can = can || (() => true);
  const [courses, setCourses] = useState([]);
  const [subView, setSubView] = useSubView(null, { encode: encodeCourseView, decode: decodeCourseView });

  // A trainer is an admin with assigned_courses (restricted access)
  const isTrainer = user && Array.isArray(user.assigned_courses) && user.assigned_courses.length > 0;

  const load = () => api.courses().then(setCourses).catch(() => {});
  useEffect(() => { load(); }, []);

  // Trainer viewing a course in student-like mode
  if (subView?.mode === "trainer") {
    return <TrainerCourseView courseId={subView.id} toast={toast} onBack={() => setSubView(null)} />;
  }

  if (subView?.mode === "edit" && subView.id === "new") {
    return <CourseEditor isNew toast={toast} onBack={(newId) => { setSubView(newId ? { mode: "edit", id: newId } : null, { replace: true }); load(); }} />;
  }
  if (subView?.mode === "edit") {
    return <CourseEditor courseId={subView.id} viewOnly={!!subView.viewOnly} toast={toast} onBack={() => { setSubView(null); load(); }} />;
  }

  const remove = async (id) => {
    if (!confirm("Delete this course?")) return;
    try { await api.deleteCourse(id); toast("Course deleted"); load(); }
    catch (e) { toast(e.message); }
  };

  return (
    <div>
      <PageHead title="Courses" sub={isTrainer ? "Your assigned courses." : "Build and manage the catalogue."} right={
        !isTrainer && _can("edit_curriculum") ? <Btn variant="dark" onClick={() => setSubView({ mode: "edit", id: "new" })}><Icon name="plus" size={16} />Add course</Btn> : null} />
      <div className="grid sm:grid-cols-1 sm:grid-cols-2 gap-4">
        {courses.map((c) => (
          <Card key={c.id} className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">{c.name}</h3>
              <Badge>{c.ctype}</Badge>
            </div>
            <div className="text-sm text-slate-500 mt-1">{c.duration_label || "—"}</div>
            <div className="text-xs text-slate-400 mt-3">Default access: {c.default_duration_days} days</div>
            <div className="flex gap-2 mt-4">
              {isTrainer
                ? <Btn variant="outline" onClick={() => setSubView({ mode: "trainer", id: c.id })}>View curriculum</Btn>
                : <>
                    {_can("edit_curriculum") && <Btn variant="outline" onClick={() => setSubView({ mode: "edit", id: c.id })}>Edit curriculum</Btn>}
                    {!_can("edit_curriculum") && _can("course_view") && <Btn variant="outline" onClick={() => setSubView({ mode: "edit", id: c.id, viewOnly: true })}>View curriculum</Btn>}
                    {_can("duplicate_course") && <Btn variant="ghost" onClick={async () => { try { await api.duplicateCourse(c.id); toast("Course duplicated"); load(); } catch (e) { toast(e.message); } }}>Duplicate</Btn>}
                    {_can("delete_course") && <Btn variant="danger" onClick={() => remove(c.id)}>Delete</Btn>}
                  </>
              }
            </div>
          </Card>
        ))}
        {courses.length === 0 && <Card className="p-8 text-center text-slate-400 sm:col-span-2">No courses yet — add one to get started.</Card>}
      </div>
    </div>
  );
}
